import { useEffect, useRef, useState } from 'react';
import { Batch, Operation } from '../../types/sharedTypeImpl.ts';
import { CollateralDamage } from './collateralDamage.tsx';
import { Comment } from './commentSubForm.tsx';
import { FixedTools } from './fixedToolsSubForm.tsx';
import { OperationDraft, OperationFormResult, operationToDraft } from './operationDraft.ts';
import { getFormIds } from './operationFormshape.ts';
import { OpSubFormRef } from './operationSubForm.ts';
import { OperatorSubForm } from './operatorSubForm.tsx';
import { Dimension, IsScheduled, MachineComponent, MachineMainComponent, OperationType, PlusOrMinus, ToolBreakReason, ToolFailReason, ToolVariant } from './selectSubForms.tsx';
import { AdjustmentText, ConfirmedScraps, Offset, OrderNumber, PotentialScraps, ProgramEditText, ToolNumber } from './textSubForms.tsx';
import { useGetInitialOrderNumber } from './useGetInitialOrderNumber.ts';


interface Props {
    batch: Batch | null;
    operationUnderEdit?: Operation | null;
    otherOperations?: Operation[];
    isSaving: boolean;
    onOperationSubmit: (operation: OperationFormResult) => void;
}

export function OperationForm({
    batch,
    operationUnderEdit,
    otherOperations,
    isSaving,
    onOperationSubmit,
}: Props) {
    const [operation, setOperation] = useState<OperationDraft>(createInitialOperation());
    const [areFinalFieldsVisible, setAreFinalFieldsVisible] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    const subformRefs = useRef(new Map<number, OpSubFormRef>(null));

    const { formIds, isFormComplete } = getFormIds(operation);


    useGetInitialOrderNumber(batch, operationUnderEdit, setOperation);

    useEffect(() => {
        if (isFormComplete) {
            setAreFinalFieldsVisible(true);
        }
    }, [isFormComplete]);


    function createInitialOperation() {
        return operationUnderEdit
            ? operationToDraft(operationUnderEdit)
            : { details: [] };
    }

    const setRef = (key: number, ref: OpSubFormRef | null) => {
        if (ref) subformRefs.current.set(key, ref);
        else subformRefs.current.delete(key);
    }

    const handleChange = (newOperation: OperationDraft) => {
        setOperation(newOperation);
    };

    const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (!formRef.current?.reportValidity()) return;

        const operation: Partial<OperationFormResult> = {};
        const keys = Array.from(subformRefs.current.keys()).sort();
        for (const key of keys) {
            if (!subformRefs.current.get(key)!.validateWarnAndGet(operation)) return;
        }

        onOperationSubmit(operation as OperationFormResult);
    }


    return (
        <form ref={formRef}>
            {formIds.map((it, index) => (
                it === 'operationType' ? (
                    <OperationType key={it} operation={operation} onOperationChange={handleChange} batch={batch} ref={(r) => setRef(index, r)} />
                ) : it === 'machineComponent' ? (
                    <MachineComponent key={it} operation={operation} onOperationChange={handleChange} ref={(r) => setRef(index, r)} />
                ) : it === 'machineMainComponent' ? (
                    <MachineMainComponent key={it} operation={operation} onOperationChange={handleChange} ref={(r) => setRef(index, r)} />
                ) : it === 'plusOrMinus' ? (
                    <PlusOrMinus key={it} operation={operation} onOperationChange={handleChange} ref={(r) => setRef(index, r)} />
                ) : it === 'adjustmentText' ? (
                    <AdjustmentText key={it} operation={operation} onOperationChange={handleChange} ref={(r) => setRef(index, r)} />
                ) : it === 'toolVariant' ? (
                    <ToolVariant key={it} operation={operation} onOperationChange={handleChange} ref={(r) => setRef(index, r)} />
                ) : it === 'toolNumber' ? (
                    <ToolNumber key={it} operation={operation} onOperationChange={handleChange} ref={(r) => setRef(index, r)} />
                ) : it === 'dimension' ? (
                    <Dimension key={it} operation={operation} onOperationChange={handleChange} ref={(r) => setRef(index, r)} />
                ) : it === 'offset' ? (
                    <Offset key={it} operation={operation} onOperationChange={handleChange} ref={(r) => setRef(index, r)} />
                ) : it === 'collateralDamage' ? (
                    <CollateralDamage key={it} operation={operation} onOperationChange={handleChange} ref={(r) => setRef(index, r)} />
                ) : it === 'isScheduled' ? (
                    <IsScheduled key={it} operation={operation} onOperationChange={handleChange} ref={(r) => setRef(index, r)} />
                ) : it === 'toolFailReason' ? (
                    <ToolFailReason key={it} operation={operation} onOperationChange={handleChange} ref={(r) => setRef(index, r)} />
                ) : it === 'toolBreakReason' ? (
                    <ToolBreakReason key={it} operation={operation} onOperationChange={handleChange} ref={(r) => setRef(index, r)} />
                ) : it === 'programEditText' ? (
                    <ProgramEditText key={it} operation={operation} onOperationChange={handleChange} ref={(r) => setRef(index, r)} />
                ) : it === 'fixedTools' ? (
                    <FixedTools
                        key={it}
                        ref={(r) => setRef(index, r)}
                        operation={operation}
                        onOperationChange={handleChange}
                        operationUnderEdit={operationUnderEdit}
                        otherOperations={otherOperations}
                    />
                ) : null
            ))}

            {areFinalFieldsVisible && <>
                <Comment operation={operation} onOperationChange={handleChange} ref={(r) => setRef(1001, r)} />
                <PotentialScraps operation={operation} onOperationChange={handleChange} ref={(r) => setRef(1002, r)} />
                <ConfirmedScraps operation={operation} onOperationChange={handleChange} ref={(r) => setRef(1003, r)} />
                <OrderNumber operation={operation} onOperationChange={handleChange} ref={(r) => setRef(1004, r)} />
                <OperatorSubForm operation={operation} onOperationChange={handleChange} ref={(r) => setRef(1005, r)} />

                <div className='dialog--button-bar'>
                    <button className='button--large' disabled={isSaving} onClick={handleSubmit}>
                        {isSaving ? 'Submitting...' : 'Submit'}
                    </button>
                </div>
            </>}
        </form>
    );
}
